import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, FileText, Download, Eye, ShoppingCart, Edit } from 'lucide-react';
import { API_BASE_URL } from '@/services/api';
import { orderingQuotationService } from '../services/orderingQuotationService';
import { downloadPdfFromUrl } from '../utils/downloadPdf';

export const OrderingAccordion = ({ lead }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [orderings, setOrderings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!lead) return;
    const fetchOrderings = async () => {
      setLoading(true);
      try {
        const res = await orderingQuotationService.getQuotations();
        const all = res?.success && res.data ? res.data : [];
        const contactId = lead._id || lead.customId;
        const name = (lead.customerName || `${lead.firstName || ''} ${lead.lastName || ''}`).trim().toLowerCase();

        const filtered = all.filter(q => {
          if (q.status === 'Draft' || !q.quotationNo || q.quotationNo === '-') return false;
          if (contactId && (q.contactRef === contactId || q.leadId === contactId || q.clientId === contactId)) return true;
          if (name && (q.projectName || '').trim().toLowerCase() === name) return true;
          return false;
        });
        setOrderings(filtered);
      } catch (err) {
        console.error('Failed to fetch ordering quotations:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrderings();
  }, [lead]);

  if (!lead) return null;

  const hasData = orderings.length > 0;

  return (
    <div className="bg-orange-50/40 rounded-xl border border-orange-100/60 overflow-hidden mt-3">
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-orange-50/60 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="text-[10px] text-orange-600 font-bold uppercase tracking-wider flex items-center gap-2">
          <span className="w-1 h-3 bg-orange-500 rounded-full" />
          Orderings {hasData ? `Available (${orderings.length})` : 'Not Available'}
        </div>
        <div className="flex items-center gap-3">
          {isOpen ? <ChevronUp size={16} className="text-orange-400" /> : <ChevronDown size={16} className="text-orange-400" />}
        </div>
      </div>

      {isOpen && (
        <div className="px-4 pb-4 border-t border-orange-100/50 pt-3">
          {loading ? (
            <div className="text-[11px] text-slate-500 py-2">Loading prepared orderings...</div>
          ) : hasData ? (
            <div className="flex flex-col gap-3">
              {orderings.map((quote, idx) => {
                const quoteNo = quote.quotationNo || `Draft #${idx + 1}`;
                const pdfUrl = quote.pdfUrl ? (quote.pdfUrl.startsWith('http') ? quote.pdfUrl : `${API_BASE_URL}${quote.pdfUrl}`) : null;
                return (
                  <div key={quote._id || idx} className="border border-slate-200 bg-white rounded p-3 shadow-sm">
                    <div className="flex items-center justify-between mb-2 border-b pb-1">
                      <h5 className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5">
                        <ShoppingCart size={13} className="text-orange-500" />
                        Ordering #{quoteNo}
                      </h5>
                      <span className="px-2 py-0.5 border rounded-md text-[10px] font-normal bg-orange-50 text-orange-700 border-orange-200">
                        {quote.status || 'Draft'}
                      </span>
                    </div>

                    <div className="text-[11px] text-slate-600 bg-slate-50 p-2 rounded flex flex-col gap-1 mb-2">
                      <div><span className="font-semibold text-slate-700">Project Name:</span> {quote.projectName || '-'}</div>
                      <div><span className="font-semibold text-slate-700">Created Date:</span> {quote.date || '-'}</div>
                      <div><span className="font-semibold text-slate-700">Designer:</span> {quote.referenceDesigner || quote.designer || '-'}</div>
                      <div>
                        <span className="font-semibold text-slate-700">Grand Total:</span>{' '}
                        {quote.totals?.grandTotal
                          ? (quote.totals.grandTotal.toString().startsWith('₹') ? quote.totals.grandTotal : `₹${Number(quote.totals.grandTotal).toLocaleString('en-IN')}`)
                          : '-'}
                      </div>
                      <div>
                        <span className="font-semibold text-slate-700">Approval Status:</span>{' '}
                        <span className={`font-medium ${quote.approvalStatus === 'Approved' ? 'text-emerald-600' : ((quote.approvalStatus === 'Pending' || (!quote.approvalStatus && quote.sendToOwner)) ? 'text-amber-600' : 'text-slate-600')}`}>
                          {quote.approvalStatus || (quote.sendToOwner ? 'Pending' : 'Not Requested')}
                        </span>
                      </div>
                      <div>
                        <span className="font-semibold text-slate-700">Order Status:</span>{' '}
                        <span className={`font-medium ${(quote.isConfirmOrder !== false && quote.isConformOrder !== false && quote.isConfirmOrder !== 'false' && quote.isConformOrder !== 'false' && (quote.isConfirmOrder !== undefined || quote.isConformOrder !== undefined)) ? 'text-emerald-600' : 'text-slate-600'}`}>
                          {(quote.isConfirmOrder !== false && quote.isConformOrder !== false && quote.isConfirmOrder !== 'false' && quote.isConformOrder !== 'false' && (quote.isConfirmOrder !== undefined || quote.isConformOrder !== undefined)) ? 'Conformed' : 'Not Started'}
                        </span>
                      </div>
                    </div>

                    {pdfUrl ? (
                      <div className="flex items-center justify-between gap-2 text-[11px] text-orange-700 bg-orange-50/50 p-2 rounded border border-orange-100">
                        <div className="flex items-center gap-2 truncate">
                          <FileText size={14} className="text-orange-500 shrink-0" />
                          <span className="truncate">Ordering_{quoteNo}.pdf</span>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(pdfUrl, '_blank');
                            }}
                            className="w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-600 hover:text-brand-blue transition-all border border-slate-200 shadow-sm"
                            title="View PDF"
                          >
                            <Eye size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/ordering/create', { state: { ...quote, isExistingOrderUpdate: true } });
                            }}
                            className="w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-600 hover:text-orange-600 transition-all border border-slate-200 shadow-sm"
                            title="Update Order"
                          >
                            <Edit size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadPdfFromUrl(pdfUrl, `Ordering_${quoteNo}.pdf`);
                            }}
                            className="w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-600 hover:text-emerald-600 transition-all border border-slate-200 shadow-sm"
                            title="Download PDF"
                          >
                            <Download size={13} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2 text-[11px] text-slate-600 bg-slate-50 p-2 rounded border border-slate-200">
                        <div className="flex items-center gap-2 truncate">
                          <FileText size={14} className="text-slate-400 shrink-0" />
                          <span className="truncate italic">PDF not generated</span>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/ordering/create', { state: { ...quote, isExistingOrderUpdate: true } });
                            }}
                            className="w-7 h-7 rounded-md bg-white flex items-center justify-center text-slate-600 hover:text-orange-600 transition-all border border-slate-200 shadow-sm"
                            title="Update Order"
                          >
                            <Edit size={13} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 py-2">No Orderings prepared yet.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderingAccordion;
